import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Icon, Select, Toggle } from "../kit";
import { useCaps } from "../editor/CapsProvider";
import { useHotkey } from "../../shared/hooks";
import { stepVariants } from "../theme";
import type { BandLayoutPose, BandLayoutPreset, DiamondConfig } from "../../shared/api-contract";
import { applyRecipeLook, type RecipeLookId } from "./recipes/looks";
import { useRing } from "./ringContext";
import {
  applyRingState,
  pushSetStep,
  replaceRingSet,
  replayCaps,
  serializeRingSet,
  withoutWearer,
  type HistoryEntry,
  type RingSetDoc,
  type SetSnapshot,
} from "./ringState";
import { RecipeGallery } from "./recipes/RecipeGallery";
import { recipeSetDoc, rowsFor, type RingRecipe } from "./recipes/recipes";
import {
  RingManagerModal,
  RingSetProvider,
  indexAfterMove,
  ringLabel,
  type RingRow,
  type RingType,
} from "./RingManagerModal";
import { RING_TABS, TabRail, ringTabs, type TabId } from "./TabRail";
import { SpecPanel } from "./SpecPanel";
import { ActionBar } from "./ActionBar";
import { AiFab } from "./AiFab";
import { ShareFab } from "./ShareFab";
import { ProfileTab } from "./tabs/ProfileTab";
import { SizeTab } from "./tabs/SizeTab";
import { MaterialTab } from "./tabs/MaterialTab";
import { GemsTab } from "./tabs/GemsTab";
import { JointsTab } from "./tabs/JointsTab";
import { EngraveTab } from "./tabs/EngraveTab";
import { EngagementTab } from "./tabs/EngagementTab";

const TAB_TITLES: Record<TabId, { title: string; subtitle: string }> = {
  profile: { title: "Ring Profile", subtitle: "Silhouette, cross-section and edge treatment" },
  size: { title: "Size & Dimensions", subtitle: "Finger size, width and thickness" },
  material: { title: "Precious Metal", subtitle: "Alloy, finish and multi-metal splits" },
  gems: { title: "Gems", subtitle: "Stone setting, shape and placement" },
  joints: { title: "Joints", subtitle: "Metal seams and decorative grooves" },
  engrave: { title: "Engraving", subtitle: "Add a personal inscription" },
  engagement: { title: "Engagement Ring", subtitle: "Center stone, head and side stones" },
};

/** ponytail: a UI ceiling, not an engine one — `layoutRings` poses any N. Raise
 * it if anyone actually stacks more than six bands. */
const RING_MAX = 6;

/** Ring 1's seed, verbatim from the old pair toggle so a two-ring set is
 * byte-for-byte the design it used to be. Rings 2+ start from the defaults. */
const HIS_RING_SEED = {
  profileId: "E",
  widthMM: 6,
  thicknessMM: 2.0,
  materials: [{ presetId: "yellowGold", finishId: "polished" }],
};

/** Undo depth. Snapshots are whole RingStates — a few KB each. */
const HISTORY_MAX = 40;
/**
 * The viewer fires `ringStateChanged` per setter call — draft mode drops the
 * segment count but does NOT suppress the event — so a slider drag streams
 * dozens of states. Recording only once the stream goes quiet turns a whole
 * drag into one undo step.
 */
const HISTORY_QUIET_MS = 400;
/** Must outlast HISTORY_QUIET_MS: the tail of our own replay is not a user edit. */
const REPLAY_SETTLE_MS = 700;

/**
 * The Ring Builder panel: one builder where the wedding band is the base product
 * and the Engagement Ring toggle attaches the head assembly (and reveals the
 * engagement-only tab).
 *
 * Must be mounted inside `<RingProvider>` — it supplies the state, the caps and
 * the ref-counted draft controller every kit slider batches through.
 */
export function RingBuilder({
  defaultEngagement = false,
  initialSet,
}: {
  defaultEngagement?: boolean;
  /** The set a `?design=` token just restored (v43). The page replays it before
   * mounting us, so this is how the rows, arrangement and link state arrive. */
  initialSet?: RingSetDoc;
}) {
  const { state, catalogs, r, apply, refresh, engagementOn } = useRing();
  const caps = useCaps();

  const [tab, setTab] = useState<TabId>("profile");
  const [activeRing, setActiveRing] = useState(0);
  /**
   * One row per ring: its name, its product type and whether it is hidden. The
   * viewer owns the real list (`getRingCount`) and the geometry; the labels are
   * ours (there is no rename RPC — see `BandState.ringName`), so this array is
   * the row model the manager, the switcher and the saved document all read.
   */
  const [rows, setRows] = useState<RingRow[]>(() =>
    initialSet?.rings.length
      ? initialSet.rings.map((s) => ({ name: s.ringName, type: s.ringType ?? "wedding", visible: true }))
      : [{ type: "wedding", visible: true }],
  );
  const ringCount = rows.length;
  const [layout, setLayout] = useState<BandLayoutPreset>(initialSet?.layout ?? "side-by-side");
  /** v68: the photographed pose over `layout` (a recipe's), the named look and
   * the size the last recipe gave ring 1 — set-level facts the saved document
   * and the undo history carry beside `layout`. */
  const [pose, setPose] = useState<BandLayoutPose | null>(initialSet?.pose ?? null);
  const [look, setLook] = useState<RecipeLookId>(initialSet?.look ?? "studio");
  const [recipePartnerSizeUS, setRecipePartnerSizeUS] = useState<number | undefined>(initialSet?.recipePartnerSizeUS);
  const [busy, setBusy] = useState(false);
  /** v42 live link. The viewer boots unlinked, so `false` is right at mount
   * unless a restored document says otherwise. */
  const [linked, setLinked] = useState(initialSet?.linked ?? false);
  const [managerOpen, setManagerOpen] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // ── Undo / redo ─────────────────────────────────────────────────────────
  // The viewer exposes no undo RPC, so history is a stack of RingState
  // snapshots restored by replaying the setters (see ringState.ts).
  const history = useRef<HistoryEntry[]>([]);
  const cursor = useRef(-1);
  const replaying = useRef(false);
  const [steps, setSteps] = useState({ canUndo: false, canRedo: false });

  useEffect(() => {
    const timer = setTimeout(() => {
      if (replaying.current) return;
      const current = history.current[cursor.current];
      if (current && JSON.stringify(current.state) === JSON.stringify(state)) return;
      history.current = [...history.current.slice(0, cursor.current + 1), { state }].slice(-HISTORY_MAX);
      cursor.current = history.current.length - 1;
      setSteps({ canUndo: cursor.current > 0, canRedo: false });
    }, HISTORY_QUIET_MS);
    return () => clearTimeout(timer);
  }, [state]);

  /** Snapshots belong to one ring; switching rings starts a fresh timeline. */
  const resetHistory = useCallback(() => {
    history.current = [];
    cursor.current = -1;
    setSteps({ canUndo: false, canRedo: false });
  }, []);

  const step = useCallback(
    async (delta: -1 | 1) => {
      const next = cursor.current + delta;
      const entry = history.current[next];
      if (!entry || busy) return;
      const snapshot = entry.state;
      setBusy(true);
      replaying.current = true;
      cursor.current = next;
      setSteps({ canUndo: next > 0, canRedo: next < history.current.length - 1 });
      try {
        if (entry.set) await restoreSet(entry.set);
        else await applyRingState(r, snapshot, replayCaps(caps));
        await refresh();
      } catch (err) {
        console.warn("[ring] undo/redo replay failed:", err);
      } finally {
        setBusy(false);
        // ponytail: fixed settle window — the trailing ringStateChanged for our
        // own replay must not be recorded as a new user edit. A viewer-side
        // history stack would make this exact instead of merely reliable.
        setTimeout(() => { replaying.current = false; }, REPLAY_SETTLE_MS);
      }
    },
    [busy, caps, r, refresh, rows, linked],
  );

  /** What `restoreRingSet` may call on this viewer. */
  const setCaps = useMemo(
    () => ({ multiRing: caps.ringLongTailV41, link: caps.ringWaveFV42, replay: replayCaps(caps), pose: caps.ringPoseV68, look: caps.ringLookV68 }),
    [caps],
  );

  /**
   * Put a whole recorded SET back (undo/redo across a recipe): the viewer's
   * ring list is replaced, then the host's rows, arrangement, link and the
   * ring being edited follow — the ring switcher never disagrees with the
   * viewer (W1 live finding 4 was a ring the host could not see).
   */
  async function restoreSet(snap: SetSnapshot) {
    const res = await replaceRingSet(r, snap.doc, { count: rows.length, linked }, setCaps);
    const n = res.rings.length;
    const nextRows = snap.rows.slice(0, n).map((row) => ({ ...row, visible: true }));
    for (let i = 0; i < nextRows.length; i++) {
      if (!snap.rows[i]?.visible && caps.ringManagerV43) {
        await r.setRingVisible(i, false);
        nextRows[i].visible = false;
      }
    }
    setRows(nextRows);
    setLayout(res.layout ?? "side-by-side");
    setPose(res.pose ?? null);
    setLook(res.look ?? "studio");
    setRecipePartnerSizeUS(res.recipePartnerSizeUS);
    setLinked(!!res.linked);
    const active = Math.min(snap.active, n - 1);
    if (active !== 0) await r.setActiveRing(active);
    setActiveRing(active);
  }

  // ── "Start from a design" (band designs W4) ──────────────────────────────
  const [recipesOpen, setRecipesOpen] = useState(false);

  /**
   * Apply a recipe THROUGH THE HOST: the current set is recorded, the viewer's
   * set is replaced by the recipe's (ring 0 + the partner, posed by the
   * recipe's layout, the wearer's own ring size and inscription kept), the
   * host's ring list / arrangement / link follow, and the whole change is ONE
   * undo step. Every control stays live afterwards — a recipe only sets them.
   */
  const applyRecipe = useCallback(async (recipe: RingRecipe) => {
    if (busy || !caps.ringRecipesV67) return;
    setBusy(true);
    replaying.current = true;
    try {
      const nowSet = { rows, active: activeRing, layout, linked, pose, look, recipePartnerSizeUS };
      const before = await serializeRingSet(r, nowSet);
      const { left, ...doc } = recipeSetDoc(recipe, {
        ring0: before.rings[0],
        partnerSizeUS: before.rings[1]?.ringSizeUS,
        recipePartnerSizeUS,
      }, caps);
      for (const why of left) console.info("[ring] design:", why);
      const res = await replaceRingSet(r, doc, { count: rows.length, linked }, setCaps);
      const nextRows = rowsFor(res);
      const nextLayout = res.layout ?? layout;
      const nextExtras = { pose: res.pose ?? null, look: res.look ?? "studio", recipePartnerSizeUS: res.recipePartnerSizeUS };
      setRows(nextRows);
      setActiveRing(0);
      setLayout(nextLayout);
      setPose(nextExtras.pose);
      setLook(nextExtras.look);
      setRecipePartnerSizeUS(nextExtras.recipePartnerSizeUS);
      setLinked(false);
      setRecipesOpen(false);
      const after = await serializeRingSet(r, { rows: nextRows, active: 0, layout: nextLayout, linked: false, ...nextExtras });
      const fresh = await r.getRingState();
      await refresh();
      const next = pushSetStep(
        history.current,
        cursor.current,
        state,
        { doc: before, rows, active: activeRing },
        { state: fresh ?? after.rings[0], set: { doc: after, rows: nextRows, active: 0 } },
      );
      history.current = next.history;
      cursor.current = next.cursor;
      setSteps({ canUndo: next.cursor > 0, canRedo: false });
    } catch (err) {
      console.warn("[ring] could not apply the design:", err);
    } finally {
      setBusy(false);
      setTimeout(() => { replaying.current = false; }, REPLAY_SETTLE_MS);
    }
  }, [activeRing, busy, caps, layout, linked, look, pose, r, recipePartnerSizeUS, refresh, rows, setCaps, state]);

  /**
   * v68 "Studio look": put the builder's own studio light back after a
   * recipe's catalogue look — one undo step (the set is recorded on both
   * sides, like a recipe), the rings and the pose untouched.
   */
  const applyStudioLook = useCallback(async () => {
    if (busy || look === "studio" || !caps.ringLookV68) return;
    setBusy(true);
    replaying.current = true;
    try {
      const nowSet = { rows, active: activeRing, layout, linked, pose, look, recipePartnerSizeUS };
      const before = await serializeRingSet(r, nowSet);
      await applyRecipeLook(r, "studio");
      setLook("studio");
      const after = await serializeRingSet(r, { ...nowSet, look: "studio" });
      const fresh = await r.getRingState();
      const next = pushSetStep(
        history.current,
        cursor.current,
        state,
        { doc: before, rows, active: activeRing },
        { state: fresh ?? after.rings[activeRing] ?? after.rings[0], set: { doc: after, rows, active: activeRing } },
      );
      history.current = next.history;
      cursor.current = next.cursor;
      setSteps({ canUndo: next.cursor > 0, canRedo: false });
    } catch (err) {
      console.warn("[ring] could not restore the studio look:", err);
    } finally {
      setBusy(false);
      setTimeout(() => { replaying.current = false; }, REPLAY_SETTLE_MS);
    }
  }, [activeRing, busy, caps.ringLookV68, layout, linked, look, pose, r, recipePartnerSizeUS, rows, state]);

  // ── The ring set (v41: a list, was a pair) ───────────────────────────────
  // A pre-v41 viewer only knows addSecondRing/removeSecondRing, so it is capped
  // at a pair and the two shims do exactly what they always did.
  const maxRings = caps.ringLongTailV41 ? RING_MAX : 2;

  const selectRing = useCallback(
    async (index: number) => {
      setActiveRing(index);
      resetHistory();
      await apply(r.setActiveRing(index));
    },
    [apply, r, resetHistory],
  );

  /**
   * Add a ring of one of the reference's three types. Wedding is a plain band;
   * memoire carries the eternity run its preset chose (through `setDiamond`, so
   * the channel/clamp settings mill their groove); engagement attaches the head.
   */
  const addRing = useCallback(async (type: RingType = "wedding", memoire?: Partial<DiamondConfig>) => {
    if (busy || ringCount >= maxRings) return;
    setBusy(true);
    resetHistory();
    // A ring added while the set is LINKED has to be brought into the design,
    // or the panel says "every design change applies to all N rings" over a
    // ring that plainly does not match. The engine never retro-copies (see the
    // contract note on setRingsLinked), so replay the design onto the new ring
    // — which, being linked, lands on all of them.
    const converge = linked && caps.ringWaveFV42;
    const snapshot = state;
    if (converge) replaying.current = true;
    try {
      // The His seed stays the second WEDDING band's arrival state; a memoire or
      // an engagement ring has its own character and starts from the defaults.
      const base = !converge && type === "wedding" && ringCount === 1 ? HIS_RING_SEED : {};
      await apply(
        caps.ringLongTailV41
          ? r.addRing({ ...base, ringType: type })
          : r.addSecondRing(base),
      );
      setActiveRing(ringCount);
      setRows([...rows, { type, visible: true }]);
      if (converge) {
        // The design only: the new ring keeps its OWN type and name. v67's
        // `loadRingState` restores the labels a state carries (undo, a shared
        // ring), and this snapshot is the previously active ring's.
        await applyRingState(r, withoutWearer(snapshot), replayCaps(caps));
        await refresh();
      }
      if (memoire) await apply(r.setDiamond(memoire));
      if (type === "engagement" && caps.ringBuilderV37) {
        await apply(r.setEngagementEnabled(true));
        setTab("engagement");
        try { await r.resetView(); } catch { /* older viewer */ }
      }
    } catch (err) {
      console.warn("[ring] could not add a ring:", err);
    } finally {
      setBusy(false);
      if (converge) setTimeout(() => { replaying.current = false; }, REPLAY_SETTLE_MS);
    }
  }, [apply, busy, caps, linked, maxRings, r, refresh, resetHistory, ringCount, rows, state]);

  /** Delete one row's ring (v43: any row, ring 0 included — the engine promotes
   * the next ring into the base slot). A pre-v41 viewer can only drop ring 1. */
  const removeRing = useCallback(async (index: number) => {
    if (busy || ringCount < 2) return;
    const victim = caps.ringLongTailV41 ? index : 1;
    setBusy(true);
    resetHistory();
    try {
      if (caps.ringLongTailV41) await r.removeRing(victim);
      else await r.removeSecondRing();
      // Same rule the engine applies: deleting the ring being edited hands the
      // edits to ring 0, a lower one shifts the index down with its ring.
      const next = victim === activeRing ? 0 : activeRing > victim ? activeRing - 1 : activeRing;
      setRows(rows.filter((_, i) => i !== victim));
      setActiveRing(next);
      await apply(r.setActiveRing(next));
    } catch (err) {
      console.warn("[ring] could not remove a ring:", err);
    } finally {
      setBusy(false);
    }
  }, [activeRing, apply, busy, caps.ringLongTailV41, r, resetHistory, ringCount, rows]);

  /** Move ←/→. Design-neutral, so the undo timeline survives it. */
  const moveRing = useCallback(async (from: number, to: number) => {
    if (busy || !caps.ringManagerV43 || from === to) return;
    if (to < 0 || to >= ringCount) return;
    setBusy(true);
    try {
      await apply(r.moveRing(from, to));
      const next = [...rows];
      next.splice(to, 0, ...next.splice(from, 1));
      setRows(next);
      // The engine moves its own edit index with the ring; say it out loud anyway
      // so ONE formula decides which ring the panel is editing.
      const active = indexAfterMove(activeRing, from, to);
      setActiveRing(active);
      await apply(r.setActiveRing(active));
    } catch (err) {
      console.warn("[ring] could not move a ring:", err);
    } finally {
      setBusy(false);
    }
  }, [apply, busy, caps.ringManagerV43, r, ringCount, rows]);

  /** "Hide ring": the ring keeps its slot and its state, and the layout closes
   * up around it. */
  const setRingVisible = useCallback(async (index: number, visible: boolean) => {
    if (busy || !caps.ringManagerV43) return;
    setBusy(true);
    try {
      await apply(r.setRingVisible(index, visible));
      setRows(rows.map((row, i) => (i === index ? { ...row, visible } : row)));
    } catch (err) {
      console.warn("[ring] could not hide a ring:", err);
    } finally {
      setBusy(false);
    }
  }, [apply, busy, caps.ringManagerV43, r, rows]);

  /** Rename is host-only: the label lives on `BandState.ringName`, which has no
   * setter — it travels with the saved document and an `addRing` seed. */
  const renameRing = useCallback((index: number, name: string) => {
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, name } : row)));
  }, []);

  /**
   * v42 LIVE link (`setRingsLinked`): while on, every design setter applied to
   * the ring being edited is mirrored to the others ENGINE-side — ring size and
   * the engraving TEXT deliberately excepted, because those belong to a wearer
   * and not to the design (the exclusion list is on the contract method).
   *
   * The engine does not retro-copy on link, so switching it on replays the
   * active ring's own design once: with the link live, that single replay fans
   * out and the set converges.
   */
  const toggleLink = useCallback(async (on: boolean) => {
    if (busy) return;
    const snapshot = state;
    setBusy(true);
    // The states streamed by the converge replay are not new user edits.
    replaying.current = true;
    try {
      await apply(r.setRingsLinked(on));
      setLinked(on);
      if (on) {
        await applyRingState(r, snapshot, replayCaps(caps));
        await refresh();
      }
    } catch (err) {
      console.warn("[ring] could not link the rings:", err);
    } finally {
      setBusy(false);
      setTimeout(() => { replaying.current = false; }, REPLAY_SETTLE_MS);
    }
  }, [apply, busy, caps, r, refresh, state]);

  /** Pre-v42 fallback: a one-shot copy onto the other ring. No engine-side
   * mirror exists there, and with three rings "the other one" has no answer —
   * hence the pair-only gate on the button. */
  const matchRings = useCallback(async () => {
    if (busy) return;
    const other = activeRing === 0 ? 1 : 0;
    const snapshot = state;
    setBusy(true);
    // The states streamed while the OTHER ring is selected are not this ring's
    // timeline — keep them out of the undo history.
    replaying.current = true;
    try {
      await apply(r.setActiveRing(other));
      await applyRingState(r, withoutWearer(snapshot), replayCaps(caps));
      await apply(r.setActiveRing(activeRing));
      await refresh();
    } catch (err) {
      console.warn("[ring] could not copy the design across:", err);
    } finally {
      setBusy(false);
      setTimeout(() => { replaying.current = false; }, REPLAY_SETTLE_MS);
    }
  }, [activeRing, apply, busy, caps, r, refresh, state]);

  // ── Engagement toggle ────────────────────────────────────────────────────
  const setEngagement = useCallback(
    async (on: boolean) => {
      try {
        await apply(r.setEngagementEnabled(on));
        if (on) {
          setTab("engagement");
          // RE-FRAME. Attaching a head makes the ring roughly half as tall
          // again, and the camera was placed for a band: at boot the stone was
          // cut off by the top of the viewport, which is the first thing anyone
          // turning this on saw. The engine re-frames correctly when asked — it
          // is simply never asked, because nothing else about the ring changes
          // its bounds this much.
          try { await r.resetView(); } catch { /* older viewer */ }
        }
      } catch (err) {
        // A pre-v37 viewer rejects the method name outright (it does not hang).
        console.warn("[ring] setEngagementEnabled unavailable:", err);
      }
    },
    [apply, r],
  );

  // ⌘/Ctrl+Z, ⇧ for redo. `useHotkey` already ignores keystrokes aimed at an
  // input, so typing "z" in the inscription or a ring name is safe.
  useHotkey(
    "z",
    useCallback((e: KeyboardEvent) => {
      e.preventDefault();
      void step(e.shiftKey ? 1 : -1);
    }, [step]),
    { meta: true },
  );

  // `?engagement=1` — enable once, on first mount, if it isn't already on.
  const seeded = useRef(false);
  useEffect(() => {
    if (seeded.current) return;
    seeded.current = true;
    if (defaultEngagement && !engagementOn && caps.ringBuilderV37) void setEngagement(true);
  }, [caps.ringBuilderV37, defaultEngagement, engagementOn, setEngagement]);

  // Reset the body scroll whenever the tab changes (band wizard behaviour).
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = 0; }, [tab]);

  // TabRail resets a stranded selection itself; keep the body in step for the
  // render before that effect lands, so EngagementTab never draws headless.
  const tabs = ringTabs(engagementOn && caps.ringBuilderV37, state.bandStyle === "crossover");
  const activeTab: TabId = tabs.some((t) => t.id === tab) ? tab : RING_TABS[0].id;
  const meta = TAB_TITLES[activeTab];

  const engagementReady = caps.ringBuilderV37;

  // What the footer's save document and its per-ring export read.
  const set = useMemo(
    () => ({ rows, active: activeRing, layout, linked, pose, look, recipePartnerSizeUS }),
    [rows, activeRing, layout, linked, pose, look, recipePartnerSizeUS],
  );

  return (
    <RingSetProvider value={set}>
      <section className="flex w-[440px] shrink-0 flex-col border-r border-outline-variant/30 bg-surface-container-low/40">
        {/* ── Brand bar ── */}
        {/* No panel brand bar: the page header directly above already reads
            "Ring Builder", and repeating it cost 53px of the one column that is
            actually short — the tab body. The mode subtitle moved up there too. */}

        {/* ── Top bar: ring switch · pair · undo/redo ── */}
        <div className="flex items-center gap-2 px-6 py-3">
          <div className="flex min-w-0 flex-1 items-center gap-1 rounded-full border border-outline-variant/50 bg-surface p-1">
            {rows.map((row, i) => (
              <RingSwitch
                key={i}
                label={ringLabel(rows, i)}
                active={activeRing === i}
                hidden={!row.visible}
                onClick={() => void selectRing(i)}
              />
            ))}
          </div>

          {ringCount > 1 && caps.ringWaveFV42 ? (
            <button
              type="button"
              title={linked ? "Unlink — edit each ring on its own" : "Link the rings — one design, separate sizes"}
              aria-label="Link the rings"
              aria-pressed={linked}
              disabled={busy}
              onClick={() => void toggleLink(!linked)}
              className={`shrink-0 rounded-full p-1 transition-colors disabled:opacity-40 ${
                linked ? "text-secondary" : "text-on-surface-variant hover:text-primary"
              }`}
            >
              <Icon name={linked ? "link" : "link_off"} size={17} fill={linked} />
            </button>
          ) : ringCount === 2 ? (
            <button
              type="button"
              title="Copy this ring's design to the other ring"
              aria-label="Copy this ring's design to the other ring"
              disabled={busy}
              onClick={() => void matchRings()}
              className="shrink-0 rounded-full p-1 text-on-surface-variant transition-colors hover:text-primary disabled:opacity-40"
            >
              <Icon name="link" size={17} />
            </button>
          ) : null}

          {/* One button for the whole set: the reference's "+ Add ring" opens the
              manager, which is also where rename, hide, reorder and per-row
              delete live — so the old bare add/remove pair is gone. */}
          <RoundButton
            icon="add"
            title="Add or remove rings"
            disabled={busy}
            onClick={() => setManagerOpen(true)}
          />
          <RoundButton icon="undo" title="Undo (Ctrl+Z)" disabled={!steps.canUndo || busy} onClick={() => void step(-1)} />
          <RoundButton icon="redo" title="Redo (Ctrl+Shift+Z)" disabled={!steps.canRedo || busy} onClick={() => void step(1)} />
        </div>

        {linked && ringCount > 1 ? (
          <span className="-mt-1 px-6 pb-2 font-body text-[10px] text-outline">
            Linked: every design change applies to all {ringCount} rings. Ring size and the
            engraved words stay each ring&rsquo;s own.
          </span>
        ) : null}

        {caps.ringRecipesV67 ? (
          <div className="px-6 pb-3">
            <button
              type="button"
              disabled={busy}
              onClick={() => setRecipesOpen(true)}
              className="flex w-full items-center gap-2 rounded-full border border-outline-variant/50 px-4 py-2 font-display text-[11px] font-semibold uppercase tracking-[0.12em] text-on-surface-variant transition-colors hover:border-primary hover:text-primary disabled:opacity-40"
            >
              <Icon name="dashboard_customize" size={16} />
              Start from a design
            </button>
          </div>
        ) : null}

        {/* ── Engagement Ring toggle ── */}
        <div
          className={`flex items-center gap-3 border-y border-outline-variant/30 px-6 py-3 ${
            engagementReady ? "" : "pointer-events-none opacity-50"
          }`}
          title={engagementReady ? undefined : "Requires viewer contract v37+"}
        >
          <Icon name="auto_awesome" size={17} className="text-on-surface-variant" />
          <div className="flex flex-1 flex-col">
            <span className="font-display text-[11px] font-semibold uppercase tracking-[0.12em] text-on-surface-variant">
              Engagement Ring
            </span>
            <span className="font-body text-[10px] text-outline">
              {engagementOn ? "Head assembly attached" : "Plain wedding band"}
            </span>
          </div>
          <Toggle
            checked={engagementOn}
            label="Engagement Ring"
            onChange={(v) => void setEngagement(v)}
          />
        </div>

        {/* ── Arrangement (only means anything with more than one ring) ── */}
        {ringCount > 1 ? (
          <div className="flex items-center gap-3 border-b border-outline-variant/30 px-6 py-3">
            <Icon name="view_in_ar" size={16} className="text-on-surface-variant" />
            <span className="shrink-0 font-display text-[10px] font-semibold uppercase tracking-[0.12em] text-on-surface-variant">
              Arrangement
            </span>
            <div className="flex-1">
              <Select
                value={layout}
                options={pose
                  // v68: while a recipe's photographed pose is on, the preset reads "· as photographed".
                  ? catalogs.bandLayouts.map((o) => (o.id === layout ? { ...o, label: `${o.label} · as photographed` } : o))
                  : catalogs.bandLayouts}
                variant="boxed"
                label="Ring set arrangement"
                onChange={(id) => {
                  // Choosing an arrangement returns to its shared law: the pose
                  // is cleared (a call without one), the look is kept.
                  setLayout(id as BandLayoutPreset);
                  setPose(null);
                  void r.setBandLayout(id as BandLayoutPreset);
                }}
              />
            </div>
          </div>
        ) : null}

        <TabRail active={activeTab} onSelect={setTab} />

        {/* ── Active tab body ── */}
        <div ref={scrollRef} className="scroll-thin min-h-0 flex-1 overflow-y-auto px-6 py-6">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              variants={stepVariants}
              initial="initial"
              animate="enter"
              exit="exit"
              className="flex flex-col gap-5"
            >
              <header className="flex flex-col gap-1">
                <h1 className="font-display text-headline-md text-primary">{meta.title}</h1>
                <p className="font-body text-[12px] text-on-surface-variant">{meta.subtitle}</p>
              </header>
              <TabBody tab={activeTab} />
            </motion.div>
          </AnimatePresence>
        </div>

        <SpecPanel activeRing={activeRing} ringCount={ringCount} />
        <ActionBar activeRing={activeRing} ringCount={ringCount} />
      </section>

      {/* Siblings of the panel, not inside the scrolling tab body: both FABs are
          fixed to the viewport over the viewer stage — AI bottom-left, Share
          bottom-right. */}
      <AiFab />
      <ShareFab />

      {recipesOpen ? (
        <RecipeGallery
          busy={busy}
          onClose={() => setRecipesOpen(false)}
          onApply={(rc) => void applyRecipe(rc)}
          onStudioLook={caps.ringLookV68 && look !== "studio" ? () => void applyStudioLook() : undefined}
        />
      ) : null}

      {managerOpen ? (
        <RingManagerModal
          rows={rows}
          active={activeRing}
          busy={busy}
          canAdd={ringCount < maxRings}
          canManage={caps.ringManagerV43}
          canDelete={(i) => ringCount > 1 && (i === 0 ? caps.ringManagerV43 : caps.ringLongTailV41 || i === 1)}
          onClose={() => setManagerOpen(false)}
          onSelect={(i) => void selectRing(i)}
          onRename={renameRing}
          onVisible={(i, v) => void setRingVisible(i, v)}
          onMove={(from, to) => void moveRing(from, to)}
          onDelete={(i) => void removeRing(i)}
          onAdd={(type, memoire) => void addRing(type, memoire)}
        />
      ) : null}
    </RingSetProvider>
  );
}

function TabBody({ tab }: { tab: TabId }) {
  switch (tab) {
    case "profile": return <ProfileTab />;
    case "size": return <SizeTab />;
    case "material": return <MaterialTab />;
    case "gems": return <GemsTab />;
    case "joints": return <JointsTab />;
    case "engrave": return <EngraveTab />;
    case "engagement": return <EngagementTab />;
  }
}

function RingSwitch({
  label, active, hidden, onClick,
}: { label: string; active: boolean; hidden?: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      title={hidden ? `${label} — hidden` : label}
      className={`relative min-w-0 flex-1 truncate rounded-full px-2 py-1.5 font-display text-[11px] font-semibold uppercase tracking-[0.1em] transition-colors ${
        active ? "text-primary" : "text-on-surface-variant/70 hover:text-primary"
      } ${hidden ? "line-through decoration-1 opacity-50" : ""}`}
    >
      {active ? (
        <motion.span
          layoutId="ring-switch-active"
          className="absolute inset-0 rounded-full bg-surface-container-high"
        />
      ) : null}
      <span className="relative">{label}</span>
    </button>
  );
}

function RoundButton({
  icon,
  title,
  disabled,
  onClick,
}: {
  icon: string;
  title: string;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      disabled={disabled}
      onClick={onClick}
      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-outline-variant/50 text-on-surface-variant transition-colors hover:border-primary hover:text-primary disabled:opacity-30 disabled:hover:border-outline-variant/50 disabled:hover:text-on-surface-variant"
    >
      <Icon name={icon} size={17} />
    </button>
  );
}
